// Pieces shared by the recipe page and the cooking layout (redesign slice 4,
// boards 29 and wireframe 7): the servings stepper, the ingredient list with
// its amount column, and numbered steps. The lines themselves come from
// src/lib/recipeDisplay.ts.
import type { CSSProperties, ReactNode } from "react";
import { COLORS, FONT_SIZE, LAYOUT, RADIUS, SPACE } from "../lib/constants";
import type { IngredientLine, StepLine } from "../lib/recipeDisplay";
import { sectionHeading, smallLabel, supporting } from "./recipeStyles";


const round44: CSSProperties = {
  width: LAYOUT.tapTarget,
  height: LAYOUT.tapTarget,
  borderRadius: RADIUS.pill,
  border: `1px solid ${COLORS.line}`,
  background: COLORS.card,
  color: COLORS.ink,
  fontSize: FONT_SIZE.h3,
  lineHeight: 1,
  padding: 0,
  cursor: "pointer",
};

export function ServingsStepper({ servings, onChange, label = "Serves" }: { servings: number; onChange: (n: number) => void; label?: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: SPACE.md, fontSize: FONT_SIZE.base, flexShrink: 0 }}>
      <button type="button" aria-label="Fewer servings" disabled={servings <= 1} onClick={() => onChange(Math.max(1, servings - 1))} style={{ ...round44, opacity: servings <= 1 ? 0.5 : 1 }}>
        −
      </button>
      <span aria-live="polite">
        {label} <strong>{servings}</strong>
      </span>
      <button type="button" aria-label="More servings" onClick={() => onChange(servings + 1)} style={round44}>
        +
      </button>
    </span>
  );
}

const groupLabel: CSSProperties = { ...smallLabel, color: COLORS.accent, marginTop: SPACE.md };
const note: CSSProperties = { fontFamily: "var(--font-serif)", fontStyle: "italic", fontWeight: 500, fontSize: FONT_SIZE.base, color: COLORS.soft, padding: "var(--space-sm) 0" };

// Amount in its own bold, tabular column so quantities line up.
export function IngredientList({ lines, large = false }: { lines: IngredientLine[]; large?: boolean }) {
  if (lines.length === 0) return <p style={{ ...supporting, margin: 0 }}>No ingredients listed.</p>;
  const size = large ? FONT_SIZE.xl : FONT_SIZE.base;
  return (
    <div>
      {lines.map((line) => {
        if (line.kind === "header") return <div key={line.key} style={groupLabel}>{line.label}</div>;
        if (line.kind === "note") return <div key={line.key} style={note}>{line.text}</div>;
        return (
          <div key={line.key} style={{ display: "grid", gridTemplateColumns: "minmax(64px, auto) minmax(0, 1fr)", columnGap: SPACE.xl, padding: "var(--space-md) 0", borderBottom: `1px solid ${COLORS.line}`, fontSize: size }}>
            <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums lining-nums", whiteSpace: "nowrap" }}>{line.amount}</span>
            <span>{line.text}</span>
          </div>
        );
      })}
    </div>
  );
}

const stepNumber: CSSProperties = {
  fontFamily: "var(--font-serif)",
  fontSize: FONT_SIZE.h4,
  fontWeight: 700,
  color: COLORS.accent,
  fontVariantNumeric: "lining-nums tabular-nums",
  textAlign: "right",
};

export function StepList({ lines }: { lines: StepLine[] }) {
  if (lines.length === 0) return <p style={{ ...supporting, margin: 0 }}>No instructions added yet.</p>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "24px minmax(0, 1fr)", columnGap: SPACE.xl, rowGap: SPACE.xl2, alignItems: "baseline" }}>
      {lines.map((line) => {
        if (line.kind === "header") return <div key={line.key} style={{ ...groupLabel, gridColumn: "1 / -1" }}>{line.label}</div>;
        if (line.kind === "note") return <div key={line.key} style={{ ...note, gridColumn: "2", padding: 0 }}>{line.text}</div>;
        return <StepRow key={line.key} number={line.number} text={line.text} />;
      })}
    </div>
  );
}

function StepRow({ number, text }: { number: number; text: string }) {
  return (
    <>
      <span style={stepNumber}>{number}</span>
      <span style={{ fontSize: FONT_SIZE.base, lineHeight: 1.45 }}>{text}</span>
    </>
  );
}

export function Chips({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: SPACE.md, flexWrap: "wrap" }}>
      {items.map((t) => (
        <span key={t} style={{ fontSize: FONT_SIZE.xs, color: COLORS.soft, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.pill, padding: "var(--space-hair) var(--space-lg)" }}>
          {t}
        </span>
      ))}
    </div>
  );
}

export function Section({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: SPACE.md, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.xl, flexWrap: "wrap", minHeight: LAYOUT.tapTarget }}>
        <h2 style={sectionHeading}>{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}
